import React, { useRef, useState } from 'react';
import TypewriterText from './TypewriterText';
import useProtocolPlayback from './useProtocolPlayback';
import './AIProtocol.css';

interface ProtocolStep {
  pending: string;
  complete: string;
}

export interface ProtocolScenario {
  id: string;
  name: string;
  prompt: string;
  intro: string;
  lines: string[];
  source: string | null;
  materials: ProtocolStep;
  processing: ProtocolStep;
  result: ProtocolStep;
  completionStatus?: string;
}

const ProtocolIcon: React.FC<{ kind: 'workspace' | 'check' | 'file' | 'replay' }> = ({ kind }) => (
  <svg
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.6"
    viewBox="0 0 24 24"
  >
    {kind === 'workspace' && <path d="M4 3h6v6H4zM14 15h6v6h-6zM7 9v9h7M10 6h7v9" />}
    {kind === 'check' && <path d="m5 12 4 4L19 6" />}
    {kind === 'file' && <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8l-5-5Zm0 0v5h5M9 12h6M9 16h6" />}
    {kind === 'replay' && <path d="M3 10a9 9 0 1 1 2 8M3 4v6h6" />}
  </svg>
);

const AIProtocol: React.FC<{
  scenario: ProtocolScenario;
  active: boolean;
  onReplay?: () => void;
}> = ({ scenario, active, onReplay }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [replay, setReplay] = useState(0);
  const segments = [scenario.intro, ...scenario.lines];
  const characterCount = segments.reduce((count, text) => count + text.length, 0);
  const { stage, length, playing } = useProtocolPlayback(containerRef, active, characterCount, replay);
  const steps = [
    { title: 'Материалы', ...scenario.materials },
    { title: 'Обработка', ...scenario.processing },
    { title: 'Результат', ...scenario.result },
  ];
  const statuses = [
    'Находим материалы',
    'Разбираем задачу',
    scenario.result.pending,
    scenario.completionStatus || scenario.result.complete,
  ];
  const renderText = (text: string, index: number) => {
    const offset = segments.slice(0, index).reduce((count, segment) => count + segment.length, 0);
    return (
      <TypewriterText
        length={length - offset}
        text={text}
        typing={stage === 2 && length >= offset && length < offset + text.length}
      />
    );
  };

  return (
    <div
      ref={containerRef}
      aria-hidden={!active}
      aria-label="Пример работы ИИ: протокол задачи"
      className={`ai-protocol ${active ? 'is-active' : ''} ${playing ? 'is-playing' : ''}`}
      role="group"
    >
      <div className="protocol-bar">
        <span className="protocol-brand">
          <ProtocolIcon kind="workspace" />
          A2 / INTELLIGENCE
        </span>
        <span className="protocol-kind">{scenario.name}</span>
      </div>
      <div className="protocol-task">
        <span className="protocol-label">ЗАДАЧА</span>
        <p>{scenario.prompt}</p>
      </div>
      <div className="protocol-workspace">
        <ol
          aria-label="Этапы обработки"
          className="protocol-steps"
        >
          {steps.map((step, index) => {
            let state = 'waiting';
            let stateLabel = 'ожидает';
            if (index < stage) {
              state = 'complete';
              stateLabel = 'выполнено';
            } else if (index === stage) {
              state = 'running';
              stateLabel = 'выполняется';
            }
            return (
              <li
                key={step.title}
                aria-current={index === stage ? 'step' : undefined}
                className={`protocol-step is-${state}`}
              >
                <span
                  aria-hidden="true"
                  className="protocol-step-icon"
                >
                  {state === 'complete' ? <ProtocolIcon kind="check" /> : <span />}
                </span>
                <span className="protocol-step-title">
                  {step.title}
                  <span className="sr-only">{`: ${stateLabel}`}</span>
                </span>
                <span className="protocol-step-detail">
                  <span
                    aria-hidden={state !== 'complete'}
                    className={state === 'complete' ? 'is-visible' : ''}
                  >
                    {step.complete}
                  </span>
                  <span
                    aria-hidden={state !== 'running'}
                    className={state === 'running' ? 'is-visible' : ''}
                  >
                    {step.pending}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
        <div className="protocol-answer">
          <p className="protocol-intro">{renderText(scenario.intro, 0)}</p>
          <ol className="protocol-list">
            {scenario.lines.map((line, index) => {
              const offset = segments.slice(0, index + 1).reduce((count, segment) => count + segment.length, 0);
              return (
                <li key={line}>
                  <span
                    aria-hidden="true"
                    className={`protocol-number ${length > offset ? 'is-visible' : ''}`}
                  >
                    {`0${index + 1}`}
                  </span>
                  {renderText(line, index + 1)}
                </li>
              );
            })}
          </ol>
          <div className={`protocol-source ${stage === 3 ? 'is-visible' : ''}`}>
            <ProtocolIcon kind="file" />
            <span>{scenario.source || 'Начнём с короткого разговора с командой A2Soft.'}</span>
          </div>
        </div>
      </div>
      <div className="protocol-footer">
        <span
          aria-atomic="true"
          className="protocol-status"
          role={active ? 'status' : undefined}
        >
          <span
            aria-hidden="true"
            className="protocol-dot"
          />
          {statuses[stage]}
        </span>
        {scenario.id === 'custom' ? (
          <a
            className="protocol-contact"
            href="#Contacts"
            tabIndex={active ? 0 : -1}
          >
            Рассказать о задаче
            <span aria-hidden="true">→</span>
          </a>
        ) : (
          <button
            className="protocol-replay"
            onClick={() => {
              setReplay((value) => value + 1);
              onReplay?.();
            }}
            tabIndex={active ? 0 : -1}
            type="button"
          >
            <ProtocolIcon kind="replay" />
            Повторить ответ
          </button>
        )}
      </div>
    </div>
  );
};

export default AIProtocol;
